import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Check, X, Undo2, Banknote, Gavel, Pencil, Send, Archive, MessageSquare, Lock, Monitor, ShieldCheck } from "lucide-react";
import { api, errMsg, eur, fdate, fdatetime, fileUrl, todayIso, INVOICE_STATUS, SOURCES, PAY_MODES, ROLE_LABELS, CATEGORIES, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge, Field, Select, Spinner } from "@/components/common";
import Modal, { PromptModal } from "@/components/Modal";
import { InvoiceForm } from "@/pages/Invoices";
import { Attachments } from "@/pages/SupplierDetail";

const TONE_DOT = { green: "bg-[#2E7D5B]", navy: "bg-[#16163F]", gold: "bg-[#A5894E]", red: "bg-[#B03A3A]", gray: "bg-[#6B6B80]" };

function Timeline({ history }) {
  return (
    <div className="card overflow-x-auto p-6" data-testid="invoice-timeline">
      <h2 className="mb-5 text-sm font-bold uppercase tracking-wide text-[#6B6B80]">Frise chronologique</h2>
      <ol className="flex min-w-max items-start">
        {history.map((h, i) => (
          <li key={i} className="relative w-48 pr-4" data-testid={`timeline-step-${i}`}>
            {i < history.length - 1 && <span className="absolute left-3 top-3 h-0.5 w-full bg-[#E4E4EC]" />}
            <span className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white ${TONE_DOT[INVOICE_STATUS[h.status][1]]}`}><Check size={12} className="text-white" /></span>
            <p className="mt-3 text-sm font-bold">{h.label}</p>
            <p className="num text-xs text-[#6B6B80]">{fdatetime(h.at)}</p>
            <p className="text-xs">{h.by}{h.role && <span className="text-[#6B6B80]"> · {ROLE_LABELS[h.role]}</span>}</p>
            {h.comment && <p className="mt-1 rounded bg-[#F5F5F8] px-2 py-1 text-xs italic">« {h.comment} »</p>}
          </li>
        ))}
      </ol>
    </div>
  );
}

function Comments({ inv, reload }) {
  const [text, setText] = useState("");
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await api.post(`/invoices/${inv.id}/comments`, { text });
    setText("");
    reload();
  };
  return (
    <div className="card p-5" data-testid="invoice-comments">
      <h2 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#6B6B80]"><MessageSquare size={15} /> Échanges créateur / comptabilité</h2>
      <div className="max-h-80 space-y-3 overflow-auto">
        {inv.comments.map((c) => (
          <div key={c.id} className={`rounded-lg p-3 text-sm ${c.role === "comptable" ? "ml-6 bg-[#A5894E]/10" : "mr-6 bg-[#F5F5F8]"}`}>
            <p className="text-xs font-bold">{c.by} <span className="font-normal text-[#6B6B80]">· {ROLE_LABELS[c.role]} · {fdatetime(c.at)}</span></p>
            <p className="mt-1">{c.text}</p>
          </div>
        ))}
        {!inv.comments.length && <p className="text-xs text-[#6B6B80]">Aucun échange pour le moment.</p>}
      </div>
      <form onSubmit={send} className="mt-4 flex gap-2">
        <input className="inp" placeholder="Écrire un commentaire…" value={text} onChange={(e) => setText(e.target.value)} data-testid="comment-input" />
        <button className="btn-primary" data-testid="comment-send-btn"><Send size={15} /></button>
      </form>
    </div>
  );
}

function Imputation({ inv, settings, editable, reload }) {
  const [accs, setAccs] = useState(inv.lines.map((l) => l.account));
  useEffect(() => setAccs(inv.lines.map((l) => l.account)), [inv]);
  const save = async () => {
    try { await api.put(`/invoices/${inv.id}/imputation`, { accounts: accs }); toast.success("Imputation enregistrée"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card overflow-hidden" data-testid="invoice-lines-card">
      <div className="overflow-x-auto">
        <table className="dt">
          <thead><tr><th>Désignation</th><th>Catégorie</th><th>Qté</th><th className="text-right">PU HT</th><th>TVA</th><th className="text-right">Total HT</th><th>Compte</th></tr></thead>
          <tbody>
            {inv.lines.map((l, i) => (
              <tr key={i}>
                <td className="font-semibold">{l.designation}</td><td>{CATEGORIES[l.category]}</td><td className="num">{l.quantity}</td>
                <td className="num text-right">{eur(l.unit_price)}</td><td className="num">{String(l.vat_rate).replace(".", ",")} %</td><td className="num text-right font-semibold">{eur(l.total_ht)}</td>
                <td>
                  {editable ? (
                    <select className="inp !py-1 text-xs" value={accs[i]} onChange={(e) => setAccs(accs.map((a, j) => (j === i ? e.target.value : a)))} data-testid={`imputation-account-${i}`}>
                      {settings.accounts.map((a) => <option key={a.code} value={a.code}>{a.code} – {a.label}</option>)}
                    </select>
                  ) : <span className="mono text-xs">{l.account}</span>}
                  {l.immo && <span className="ml-1 rounded bg-[#16163F] px-1.5 py-0.5 text-[10px] font-bold text-white">Immobilisation</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="num flex flex-wrap items-center justify-end gap-6 border-t border-[#E4E4EC] p-4 text-sm">
        {editable && <button className="btn-secondary mr-auto" onClick={save} data-testid="imputation-save-btn">Enregistrer l'imputation</button>}
        <span>HT <b>{eur(inv.total_ht)}</b></span><span>TVA <b>{eur(inv.total_vat)}</b></span><span className="text-lg">TTC <b data-testid="invoice-total-ttc">{eur(inv.total_ttc)}</b></span>
      </div>
    </div>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, settings } = useAuth();
  const [inv, setInv] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [pay, setPay] = useState(null);
  const [edit, setEdit] = useState(false);
  const [assets, setAssets] = useState(null);
  const load = () => api.get(`/invoices/${id}`).then((r) => setInv(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (!inv || !settings) return <Spinner />;

  const act = async (action, extra = {}) => {
    try {
      await api.post(`/invoices/${id}/action`, { action, ...extra });
      toast.success("Statut mis à jour");
      setPrompt(null); setPay(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const st = inv.status;
  const A = [];
  if (can(user, "createur") && ["brouillon", "retournee"].includes(st)) A.push(<button key="t" className="btn-primary" onClick={() => act("transmit")} data-testid="action-transmit-btn"><Send size={16} /> Valider et transmettre à la comptabilité</button>);
  if (can(user, "comptable") && st === "transmise") A.push(<button key="b" className="btn-primary" onClick={() => act("approve_payment")} data-testid="action-approve-payment-btn"><ShieldCheck size={16} /> Bon à payer</button>);
  if (can(user, "comptable") && ["transmise", "bon_a_payer"].includes(st)) A.push(<button key="ret" className="btn-secondary" onClick={() => setPrompt({ action: "return", title: "Retourner au créateur", label: "Motif du retour (obligatoire)" })} data-testid="action-return-btn"><Undo2 size={16} /> Retourner au créateur</button>);
  if (can(user, "comptable") && st === "bon_a_payer") A.push(<button key="p" className="btn-primary" onClick={() => setPay({ payment_date: todayIso(), payment_mode: "virement", payment_ref: "" })} data-testid="action-pay-btn"><Banknote size={16} /> Enregistrer le paiement</button>);
  if (can(user, "createur") && ["brouillon", "retournee"].includes(st)) A.push(<button key="e" className="btn-secondary" onClick={() => setEdit(true)} data-testid="action-edit-btn"><Pencil size={16} /> Modifier</button>);
  if (can(user, "createur", "comptable") && !["litige", "comptabilisee", "archivee"].includes(st)) A.push(<button key="l" className="btn-ghost" onClick={() => setPrompt({ action: "dispute", title: "Mettre en litige", label: "Commentaire (obligatoire)" })} data-testid="action-dispute-btn"><Gavel size={16} /> Mettre en litige</button>);
  if (can(user, "createur", "comptable") && st === "litige") A.push(<button key="rl" className="btn-secondary" onClick={() => act("resolve")} data-testid="action-resolve-btn"><Gavel size={16} /> Lever le litige</button>);
  if (can(user, "createur") && ["brouillon", "retournee"].includes(st)) A.push(<button key="ar" className="btn-ghost" onClick={() => window.confirm("Archiver cette facture ?") && act("archive")} data-testid="action-archive-btn"><Archive size={16} /> Archiver</button>);
  const matLines = inv.lines.map((l, i) => [l, i]).filter(([l]) => l.category === "materiel");
  const m = inv.matching;

  return (
    <div className="space-y-6">
      <button className="btn-ghost" onClick={() => nav("/factures")} data-testid="invoice-back-btn"><ChevronLeft size={16} /> Factures</button>
      <div className="flex flex-wrap items-start justify-between gap-4 rise">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-extrabold tracking-tight" data-testid="invoice-number">Facture {inv.number}</h1>
            <Badge meta={INVOICE_STATUS[st]} testid="invoice-status-badge" />
            {inv.overdue && <Badge meta={["En retard", "red"]} testid="invoice-overdue-badge" />}
            {inv.locked && <span className="flex items-center gap-1 text-xs font-semibold text-[#6B6B80]"><Lock size={13} /> Verrouillée</span>}
          </div>
          <div className="mt-2 h-0.5 w-10 bg-[#A5894E]" />
          <p className="mt-3 text-sm text-[#6B6B80]">
            <button className="font-semibold text-[#16163F] underline decoration-[#A5894E] underline-offset-4" onClick={() => nav(`/fournisseurs/${inv.supplier_id}`)}>{inv.supplier_name}</button>
            {" "}· {SOURCES[inv.source]} · centre de coût {inv.cost_center}{inv.piece_number && <> · pièce <span className="mono">{inv.piece_number}</span></>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="invoice-actions">{A}</div>
      </div>
      <Timeline history={inv.history} />
      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <div className="card grid gap-4 p-5 text-sm sm:grid-cols-4">
            {[["Date de facture", fdate(inv.date)], ["Échéance", fdate(inv.due_date)], ["Bon de commande", m?.order_number || "—"], ["Paiement", inv.payment ? `${fdate(inv.payment.date)} · ${PAY_MODES[inv.payment.mode]}` : "—"]].map(([k, v]) => (
              <div key={k}><span className="lbl">{k}</span><span className="num font-bold">{v}</span></div>
            ))}
            {inv.payment?.reference && <div className="sm:col-span-4 text-xs text-[#6B6B80]">Référence de paiement : <span className="mono">{inv.payment.reference}</span></div>}
            {inv.email_sender && <div className="sm:col-span-4 text-xs text-[#6B6B80]">Reçue par email de {inv.email_sender} – « {inv.email_subject} »</div>}
          </div>
          {m && <div className={`rounded-lg border p-4 text-sm ${m.warning ? "border-[#B03A3A] bg-[#B03A3A]/10 text-[#B03A3A]" : "border-[#E4E4EC] bg-white"}`} data-testid="invoice-matching">
            <b>Rapprochement commande / facture</b> — commande {m.order_number} : {eur(m.order_total_ht)} HT · facture : {eur(m.invoice_total_ht)} HT · écart <b>{eur(m.gap)} ({String(m.gap_pct).replace(".", ",")} %)</b>
            {m.warning ? ` — dépasse le seuil de ${m.threshold} %` : " — dans la tolérance"}
          </div>}
          <Imputation inv={inv} settings={settings} editable={can(user, "comptable") && ["transmise", "bon_a_payer", "payee"].includes(st)} reload={load} />
          {can(user, "createur") && matLines.length > 0 && (
            <div className="card flex flex-wrap items-center gap-3 p-4 text-sm">
              <Monitor size={18} className="text-[#A5894E]" />
              <span className="flex-1">{inv.assets.length ? `${inv.assets.length} fiche(s) matériel liée(s) à cette facture.` : "Créer automatiquement les fiches matériel à partir des lignes de matériel."}</span>
              <button className="btn-secondary" onClick={() => setAssets(matLines.map(([, i]) => i))} data-testid="create-assets-btn">Créer les fiches matériel</button>
            </div>
          )}
          <Comments inv={inv} reload={load} />
        </div>
        <div className="space-y-6">
          <div className="card overflow-hidden" data-testid="invoice-document">
            <div className="border-b border-[#E4E4EC] px-4 py-3 text-sm font-bold uppercase tracking-wide text-[#6B6B80]">Document</div>
            {inv.file_id ? <iframe title="Facture" src={fileUrl(inv.file_id)} className="h-[560px] w-full" /> : <p className="p-8 text-center text-sm text-[#6B6B80]">Aucun PDF joint.</p>}
            {inv.original_file_id && inv.original_file_id !== inv.file_id && <a className="block border-t border-[#E4E4EC] px-4 py-2 text-xs font-semibold underline" href={fileUrl(inv.original_file_id)} target="_blank" rel="noreferrer">Fichier original du scan</a>}
          </div>
          <div className="card p-5"><Attachments entity="facture" id={inv.id} items={inv.attachments} onChange={load} editable={can(user, "createur", "comptable") && !inv.locked} /></div>
        </div>
      </div>
      {prompt && <PromptModal open onClose={() => setPrompt(null)} title={prompt.title} label={prompt.label} danger={prompt.action !== "dispute"} onSubmit={(v) => act(prompt.action, { comment: v })} testid={`prompt-${prompt.action}`} />}
      <Modal open={!!pay} onClose={() => setPay(null)} title="Enregistrer le paiement" testid="payment-modal">
        {pay && <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); act("pay", pay); }}>
          <Field label="Date de paiement"><input type="date" className="inp" required value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} data-testid="payment-date-input" /></Field>
          <Field label="Mode"><Select value={pay.payment_mode} onChange={(v) => setPay({ ...pay, payment_mode: v })} options={Object.entries(PAY_MODES).map(([value, label]) => ({ value, label }))} testid="payment-mode-select" /></Field>
          <Field label="Référence"><input className="inp" value={pay.payment_ref} onChange={(e) => setPay({ ...pay, payment_ref: e.target.value })} data-testid="payment-ref-input" /></Field>
          <div className="flex justify-end"><button className="btn-primary" data-testid="payment-confirm-btn">Confirmer le paiement</button></div>
        </form>}
      </Modal>
      <Modal open={edit} onClose={() => setEdit(false)} title={`Modifier la facture ${inv.number}`} wide testid="edit-invoice-modal">
        <InvoiceForm initial={{ id: inv.id, supplier_id: inv.supplier_id, number: inv.number, date: inv.date, due_date: inv.due_date, order_id: inv.order_id, cost_center: inv.cost_center, lines: inv.lines, file_id: inv.file_id, notes: inv.notes }} onSaved={() => { setEdit(false); load(); }} />
      </Modal>
      <Modal open={!!assets} onClose={() => setAssets(null)} title="Créer les fiches matériel" description="Une fiche par unité ; garantie de 3 ans à partir de la date de facture (modifiable ensuite)." testid="create-assets-modal">
        {assets && <div className="space-y-3">
          {matLines.map(([l, i]) => <label key={i} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={assets.includes(i)} onChange={(e) => setAssets(e.target.checked ? [...assets, i] : assets.filter((x) => x !== i))} /> {l.designation} × {l.quantity}</label>)}
          <div className="flex justify-end"><button className="btn-primary" data-testid="create-assets-confirm-btn" onClick={async () => { const r = await api.post(`/invoices/${id}/create-assets`, { line_indexes: assets }); toast.success(`${r.data.created} fiche(s) matériel créée(s)`); setAssets(null); load(); }}>Créer</button></div>
        </div>}
      </Modal>
    </div>
  );
}
